<template>
    <Button v-if="isBusy" variant="primary" :size="props.size" disabled>
        <Spinner size="xs" />
        <span>{{ $t("BOMTool.Loading") }}</span>
    </Button>
    <Button v-else-if="doesPartExist" variant="primary" :size="props.size" @click="onViewButtonClick()">
        <span>{{ $t("BOMTool.ViewBOM") }}</span>
        <Icon name="chevron-right" />
    </Button>
    <Button v-else variant="primary" :size="props.size" @click="onAddButtonClick()">
        <Icon name="plus" />
        <span>{{ $t("BOMTool.AddToBOM") }}</span>
    </Button>
    <LoginDialog ref="loginDialog" :title="$t('BOMTool.AddToBOM')" @continue="onLoginContinue" />
</template>
<script setup lang="ts">
import type { ButtonVariants } from "../ui";

const { gtag } = useGtag();
const { t } = useI18n();
const { toast } = useToast();
const guestCookie = useGuestCookie();
const loginDialog = useTemplateRef("loginDialog");
const localePath = useLocalePath();

const { fetchAnonymousBOM, saveBOMPart, getDefaultBOM, partExistsInBOM } = useBom();

const props = defineProps<{ part: Partial<BOMSearchPart>; size: ButtonVariants["size"] }>();

const isBusy = ref(false);
const doesPartExist = ref(false);

async function addPartToBOM() {
    isBusy.value = true;
    // there may be no anonymous BOM yet
    const anonBOM = fetchAnonymousBOM() || (await getDefaultBOM());
    const updatedBOM = await saveBOMPart(anonBOM, props.part);
    if (updatedBOM) {
        doesPartExist.value = true;
        toast({
            title: t("BOMTool.Success"),
            description: t("BOMTool.SuccessfullyAddedToBOM"),
            variant: "success",
        });
        gtag("event", "bom_tool_add_to_bom", {
            manufacturer_name: props.part.ManufacturerName,
            part_number: props.part.PartNumberScrubbedNonMeaningful,
        });
    }
}

async function onAddButtonClick() {
    if (doesPartExist.value) return;

    if (!guestCookie.value) {
        loginDialog.value?.openDialog();
        return;
    }

    try {
        gtag("event", "bom_tool_open_add_to_bom", { user_type: "Anonymous" });
        await addPartToBOM();
    } catch (e) {
        const error = e as Error;
        toast({ title: error.message || t("Global.GenericError"), variant: "warning" });
    } finally {
        isBusy.value = false;
    }
}

async function onViewButtonClick() {
    await navigateTo(localePath("/bom/local"));
}

async function onLoginContinue(isAuth: boolean) {
    if (isAuth) {
        toast({ description: t("BOMTool.PartAddToBomSignInMessage"), variant: "success" });
    } else {
        try {
            await addPartToBOM();
        } catch (e) {
            const error = e as Error;
            toast({ title: error.message || t("Global.GenericError"), variant: "warning" });
        } finally {
            isBusy.value = false;
        }
    }
}

onMounted(async () => {
    doesPartExist.value = partExistsInBOM(fetchAnonymousBOM(), props.part);
});
</script>
